import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type {
  SchemaChangeKind,
  SchemaChangeStatus,
  SchemaChangeTableCounts,
  SchemaDecision,
} from "@shared/types/schemaChanges";

/**
 * The words the schema change screens use (product gaps row 7, design section 4.5), in one place
 * so the drawer, the tables grid and the message bar say the same thing. Status is always colour
 * and a label (rules.md §7).
 */

export const SCHEMA_KIND_LABELS: Record<SchemaChangeKind, string> = {
  added: "Added",
  dropped: "Not sent and kept",
  type_changed: "Type changed",
  key_lost: "Key column gone",
};

/**
 * #1663: what a kind means, for the kinds a reader may misread. The import cannot know who changed
 * a column, so these say what differs, never who did it.
 */
export const SCHEMA_KIND_HELP: Partial<Record<SchemaChangeKind, string>> = {
  added:
    "The source sends this column and the table did not have it. The status says whether the load added it or held it for a decision.",
  dropped:
    "The table has this column and the source does not send it. It may have been added in the warehouse or removed at the source. The column and its data stay, and it is not loaded.",
};

type BadgeColor = Extract<
  SkyBadgeColor,
  "subtle" | "informative" | "success" | "warning" | "danger"
>;

export const SCHEMA_STATUS_PRESENTATION: Record<
  SchemaChangeStatus,
  { label: string; color: BadgeColor }
> = {
  pending: { label: "Not yet logged", color: "subtle" },
  applied: { label: "Applied", color: "success" },
  waiting: { label: "Waiting for review", color: "warning" },
  approved: { label: "Approved, next load", color: "informative" },
  ignored: { label: "Ignored", color: "subtle" },
  stopped: { label: "Stopped", color: "danger" },
  widen: { label: "Widen, next load", color: "informative" },
  rebuild: { label: "Rebuild, next full read", color: "informative" },
  done: { label: "Done", color: "success" },
};

export const SCHEMA_DECISION_LABELS: Record<SchemaDecision, string> = {
  approve: "Approve",
  ignore: "Ignore",
  unignore: "Stop ignoring",
  acknowledge: "Acknowledge",
  widen: "Widen column",
  rebuild: "Rebuild table",
};

/** What a decision tells the reader once it is recorded. */
/**
 * With "run this table now" refused (the connection busy, disabled or quiesced), the decision is
 * still saved: the decided sentence, then why no import started now (the server's message).
 */
export function runNotStartedSentence(decided: string, reason: string): string {
  return `${decided} The import did not start now: ${reason}`;
}

export function decidedSentence(
  decision: SchemaDecision,
  changed: number,
  runQueued: boolean,
): string {
  const rows = `${changed.toLocaleString()} ${changed === 1 ? "change" : "changes"}`;
  const next = runQueued
    ? "The import that carries it out is queued."
    : "The next import carries it out.";
  switch (decision) {
    case "approve":
      return `Approved ${rows}. ${next}`;
    case "ignore":
      return `Ignored ${rows}. Those columns are never loaded.`;
    case "unignore":
      return `${rows} waiting for review again.`;
    case "acknowledge":
      return `Acknowledged ${rows}.`;
    case "widen":
      return `Widen recorded for ${rows}. ${next}`;
    case "rebuild":
      return runQueued
        ? "Rebuild recorded. The full read that rebuilds the table is queued."
        : "Rebuild recorded. The next full read of the table rebuilds it.";
  }
}

/** "NUMBER(38,0) to NUMBER(38,9)", or the one type there is. */
export function typesText(before: string | null, after: string | null): string {
  if (before && after) return `${before} to ${after}`;
  return before ?? after ?? "";
}

/**
 * The tables grid's badge for one table's open changes: "Stopped: type change" (danger) when the
 * table stops, "2 to review" (warning) when changes wait, "Decided, next load" when only decided
 * rows remain. Null when there is nothing open, which is the common case.
 */
export function tableBadge(
  counts: Pick<SchemaChangeTableCounts, "waiting" | "stopped" | "queued"> | undefined,
): { label: string; color: BadgeColor } | null {
  if (!counts) return null;
  if (counts.stopped > 0) {
    return {
      label: counts.waiting > 0 ? `Stopped, ${counts.waiting} to review` : "Stopped: schema change",
      color: "danger",
    };
  }
  if (counts.waiting > 0) return { label: `${counts.waiting} to review`, color: "warning" };
  if (counts.queued > 0) return { label: "Decided, next load", color: "informative" };
  return null;
}

/** The connection page's message bar: "3 schema changes on 2 tables need a decision." */
export function decisionNeededSentence(rows: readonly SchemaChangeTableCounts[]): string | null {
  const tables = rows.filter((row) => row.waiting + row.stopped > 0);
  if (tables.length === 0) return null;
  const changes = tables.reduce((sum, row) => sum + row.waiting + row.stopped, 0);
  return `${changes.toLocaleString()} schema ${changes === 1 ? "change" : "changes"} on ${tables.length.toLocaleString()} ${tables.length === 1 ? "table" : "tables"} ${changes === 1 ? "needs" : "need"} a decision.`;
}
